/** @jsxImportSource @emotion/react */
import { Box, useTheme } from '@mui/material'
import { riskAvailableWorkspacesAtom } from '@app/state/riskAtoms'
import type { RiskWorkspace } from '@app/types'
import { useAtomValue } from 'jotai'
import { WorkspacePreview } from '../WorkspacePreview/WorkspacePreview'
import { WorkspacesSavedTable } from '../WorkspaceTable/WorkspacesTable'
import { contentStyles, warningBoxStyle } from './WorkspacesMenu.styles'
import { useEffect, useState } from 'react'
import { ErrorOutline } from '@mui/icons-material'
import { workspaceLayersExistInDialogData } from '../utils/Workspaces.utils'
import { useSelector } from 'react-redux'
import { RootState } from '@app/redux/store'
import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext'

interface WorkspacesMenuProps {
  onWorkspaceSelected: (workspace: RiskWorkspace | null) => void
  selectedWorkspace: RiskWorkspace | null
}

export const WorkspacesMenu = ({ onWorkspaceSelected, selectedWorkspace }: WorkspacesMenuProps) => {
  const theme = useTheme()
  const { layerData } = useSelector((state: RootState) => state.riskMap)
  const [warnings, setWarnings] = useState<string[]>([])
  const { activeWorkspace } = useMap()
  const { data: workspaces } = useAtomValue(riskAvailableWorkspacesAtom)

  useEffect(() => {
    const prepWarnings: string[] = []
    if (
      layerData &&
      selectedWorkspace &&
      !workspaceLayersExistInDialogData(selectedWorkspace, layerData)
    ) {
      prepWarnings.push(
        'A layer in this Workspace isn’t available due to a data update. The Workspace will be loaded without it. Please contact your Urban Intelligence Advisor to find out more.',
      )
    }
    if (activeWorkspace !== null) {
      prepWarnings.push('Loading a new workspace will replace your current one.')
    }
    setWarnings(prepWarnings)
  }, [layerData, selectedWorkspace, workspaces, activeWorkspace])

  return (
    <Box css={contentStyles(theme)}>
      <Box display="flex" flexDirection="column" flexGrow={1} maxHeight="100%">
        {warnings.map((warning, index) => (
          <Box key={index} css={warningBoxStyle(theme)} data-testid="workspace-warning-bar">
            <ErrorOutline sx={{ mr: 1, color: theme.palette.warning.main }} />
            {warning}
          </Box>
        ))}
        <Box flexGrow={1} position="relative" minHeight={0} maxHeight="100%">
          <Box position="absolute" top={0} bottom={0} left={0} right={0} overflow="auto">
            <WorkspacesSavedTable
              workspaces={workspaces || []}
              selectedWorkspace={selectedWorkspace}
              onSelected={onWorkspaceSelected}
            />
          </Box>
        </Box>
      </Box>
      <Box
        css={{
          width: '1px',
          backgroundColor: theme.palette.grey[100] + '80',
          [theme.breakpoints.down('md')]: {
            height: '1px',
            width: '100%',
          },
        }}
      />
      <WorkspacePreview selectedWorkspace={selectedWorkspace} />
    </Box>
  )
}
